import { memo, useCallback, type SyntheticEvent } from "react";
import { useNavigate } from "react-router-dom";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import IconButton from "@mui/material/IconButton";

import SectionHeader from "src-next/shared/ui/sections/SectionHeader";
import { QuestionnaireManagementTabsPanelSection } from "src-next/modules/questionnaire-builder";
import ClinicalCrmContextInfoBanner from "../../messageTemplates/components/ClinicalCrmContextInfoBanner";
import { MessageTemplatesSection } from "../../messageTemplates";
import type { FluxmedDefaultTemplateCategory } from "../../messageTemplates/utils/messageTemplateUiUtils";
import type { CrmStatisticsActiveTabId } from "../../statistics";
import type { MessageTemplate } from "../../messageTemplates";
import type { SectionDragHandleProps } from "../types";
import { AutoAwesome } from "@mui/icons-material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";

export interface ClinicalCrmTemplatesAndQuestionnairesTabsSectionProps {
  organizationId: string;
  practitionerId: string;
  activeTabId: CrmStatisticsActiveTabId;
  onChangeTab: (tabId: CrmStatisticsActiveTabId) => void;
  messageTemplates: MessageTemplate[];
  onCreateTemplate: (
    input: Pick<MessageTemplate, "name" | "preview" | "header">,
  ) => Promise<void>;
  onUpdateTemplate: (input: MessageTemplate) => Promise<void>;
  onDeleteTemplate: (templateId: string) => Promise<void>;
  onRefreshMessageTemplates: () => Promise<void>;
  isMessageTemplatesLoading: boolean;
  isCollapsed?: boolean;
  onToggleCollapsed?: () => void;
  dragHandleProps?: SectionDragHandleProps;
  /**
   * Callback executado após envio bem-sucedido de mensagem (ex.: atualizar estatísticas).
   */
  onMessageSent?: () => void;
  /** Recarrega franquia no dialog de envio após envio/compra. */
  franchiseRefreshSignal?: number;
  hasActiveMetaEndpoint?: boolean;
  isEndpointStatusLoading?: boolean;
  whatsappChannelOption?: "fluxmedDefault" | "metaEmbeddedSignup" | null;
  defaultTemplatesCategoryFocus?: FluxmedDefaultTemplateCategory | null;
  onDefaultTemplatesCategoryFocusConsumed?: () => void;
}

const ClinicalCrmTemplatesAndQuestionnairesTabsSection = memo(
  ({
    organizationId,
    practitionerId,
    activeTabId,
    onChangeTab,
    messageTemplates,
    onCreateTemplate,
    onUpdateTemplate,
    onDeleteTemplate,
    onRefreshMessageTemplates,
    isMessageTemplatesLoading,
    isCollapsed = false,
    onToggleCollapsed,
    dragHandleProps,
    onMessageSent,
    franchiseRefreshSignal = 0,
    hasActiveMetaEndpoint = false,
    isEndpointStatusLoading = false,
    whatsappChannelOption = null,
    defaultTemplatesCategoryFocus = null,
    onDefaultTemplatesCategoryFocusConsumed,
  }: ClinicalCrmTemplatesAndQuestionnairesTabsSectionProps) => {
    const navigate = useNavigate();

    const handleChange = useCallback(
      (_event: SyntheticEvent, next: CrmStatisticsActiveTabId) => {
        onChangeTab(next);
      },
      [onChangeTab],
    );

    const handleCreateQuestionnaire = useCallback(() => {
      navigate("/questionnaireBuilder");
    }, [navigate]);

    const handleEditQuestionnaire = useCallback(
      (questionnaireId: string) => {
        // Segue o padrão das páginas MVP: passar contexto via `state` (useLocation().state).
        // O builder aceita `questionnaireId` e também `id` como alias.
        navigate("/questionnaireBuilder", {
          state: {
            questionnaireId,
            id: questionnaireId,
            fromQuestionnaireManage: true,
          },
        });
      },
      [navigate],
    );

    return (
      <Box>
        <SectionHeader
          icon={<AutoAwesome sx={{ fontSize: 22, color: "primary.main" }} />}
          title="Modelos de mensagens WhatsApp e Questionários"
          subtitle="O envio de WhatsApp é realizado por meio dos modelos de mensagens. Crie e gerencie seus próprios modelos de mensagem WhatsApp."
          isCollapsed={isCollapsed}
          onCollapsedChange={
            onToggleCollapsed ? () => onToggleCollapsed() : undefined
          }
          actions={
            <IconButton
              size="small"
              aria-label="Reordenar seção"
              sx={{ cursor: "grab" }}
              {...(dragHandleProps?.attributes ?? {})}
              {...(dragHandleProps?.listeners ?? {})}
            >
              <DragIndicatorIcon />
            </IconButton>
          }
        >
          <Tabs
            value={activeTabId}
            onChange={handleChange}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              px: 1,
              py: 0.5,
              borderRadius: 0,
              borderTopLeftRadius: 6,
              borderTopRightRadius: 6,
              borderBottomLeftRadius: 0,
              borderBottomRightRadius: 0,
              border: "1px solid",
              borderBottom: 0,
              borderColor: "divider",
              bgcolor: "background.paper",
              overflow: "hidden",
              "& .MuiTab-root": {
                textTransform: "none",
                minHeight: 48,
              },
              "& .MuiTabs-indicator": {
                height: 5,
                borderRadius: 999,
              },
            }}
          >
            <Tab value="messageTemplates" label="Meus modelos" />
            <Tab value="defaultTemplates" label="Modelos Padrão" />
            <Tab value="questionnaires" label="Questionários" />
          </Tabs>

          <Box
            sx={{
              px: { xs: 1.25, sm: 1.5 },
              pt: 2,
              pb: { xs: 1.25, sm: 1.5 },
              borderRadius: 0,
              borderTopLeftRadius: 0,
              borderTopRightRadius: 0,
              borderBottomLeftRadius: 6,
              borderBottomRightRadius: 6,
              border: "1px solid",
              borderTop: 0,
              borderColor: "divider",
              bgcolor: "action.hover",
              overflow: "hidden",
            }}
          >
            <Box sx={{ p: 0 }}>
              {activeTabId === "messageTemplates" ? (
                <Stack spacing={0} sx={{ pt: 0, pb: { xs: 2, sm: 2.5 } }}>
                  <ClinicalCrmContextInfoBanner
                    variant="info"
                    title="Aprovação de modelos na Meta"
                    icon={<InfoOutlinedIcon />}
                    description="Ao criar um modelo de mensagem para WhatsApp, ele ficará com status pendente por até 24 horas até que a Plataforma Meta faça a análise de conteúdo do seu modelo. Somente modelos com status Aprovado podem ser enviados."
                  />
                  <MessageTemplatesSection
                    organizationId={organizationId}
                    practitionerId={practitionerId}
                    templateScope="organization"
                    templates={messageTemplates}
                    onCreateTemplate={async ({ input }) => {
                      await onCreateTemplate(input);
                    }}
                    onUpdateTemplate={async ({ input }) => {
                      await onUpdateTemplate(input);
                    }}
                    onDeleteTemplate={async ({ templateId }) => {
                      await onDeleteTemplate(templateId);
                    }}
                    onRefreshTemplates={onRefreshMessageTemplates}
                    isTemplatesLoading={isMessageTemplatesLoading}
                    onMessageSent={onMessageSent}
                    franchiseRefreshSignal={franchiseRefreshSignal}
                    hasActiveMetaEndpoint={hasActiveMetaEndpoint}
                    isEndpointStatusLoading={isEndpointStatusLoading}
                    whatsappChannelOption={whatsappChannelOption}
                  />
                </Stack>
              ) : activeTabId === "defaultTemplates" ? (
                <Stack spacing={0} sx={{ pt: 0, pb: { xs: 2, sm: 2.5 } }}>
                  <MessageTemplatesSection
                    organizationId={organizationId}
                    practitionerId={practitionerId}
                    templateScope="default"
                    templates={messageTemplates}
                    onCreateTemplate={async ({ input }) => {
                      await onCreateTemplate(input);
                    }}
                    onUpdateTemplate={async ({ input }) => {
                      await onUpdateTemplate(input);
                    }}
                    onDeleteTemplate={async ({ templateId }) => {
                      await onDeleteTemplate(templateId);
                    }}
                    onRefreshTemplates={onRefreshMessageTemplates}
                    isTemplatesLoading={isMessageTemplatesLoading}
                    hasActiveMetaEndpoint={hasActiveMetaEndpoint}
                    isEndpointStatusLoading={isEndpointStatusLoading}
                    whatsappChannelOption={whatsappChannelOption}
                    requestedDefaultCategory={defaultTemplatesCategoryFocus}
                    onRequestedDefaultCategoryConsumed={
                      onDefaultTemplatesCategoryFocusConsumed
                    }
                  />
                </Stack>
              ) : (
                <Stack spacing={0} sx={{ pt: 0, pb: { xs: 2, sm: 2.5 } }}>
                  <ClinicalCrmContextInfoBanner
                    variant="info"
                    title="Como enviar questionários"
                    icon={<InfoOutlinedIcon />}
                    description="Para enviar questionários para seus pacientes, crie o questionário, crie um modelo de mensagem e configure o questionário que deseja enviar para seus pacientes."
                  />
                  <QuestionnaireManagementTabsPanelSection
                    organizationId={organizationId}
                    onCreateQuestionnaire={handleCreateQuestionnaire}
                    onEditQuestionnaire={handleEditQuestionnaire}
                  />
                </Stack>
              )}
            </Box>
          </Box>
        </SectionHeader>
      </Box>
    );
  },
);

ClinicalCrmTemplatesAndQuestionnairesTabsSection.displayName =
  "ClinicalCrmTemplatesAndQuestionnairesTabsSection";

export default ClinicalCrmTemplatesAndQuestionnairesTabsSection;
